import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useInvalidateSnapshot } from "@/lib/executive/engine";
import { useAdminActivityBadges } from "@/hooks/useAdminActivityBadges";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  Download,
  Mail,
  MailCheck,
  Pencil,
  Trash2,
  Check,
  X,
  Send,
  StickyNote,
  RefreshCw,
  ArrowUpDown,
  Eye,
  EyeOff,
  Users,
  CheckCircle2,
  AlertTriangle,
  DollarSign,
  Armchair,
  Ticket,
  Search,
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useCommunicationStatusList,
  useSetCommunicationStatus,
} from "@/hooks/useCommunicationStatus";
import CommunicationStatusBadge from "./CommunicationStatusBadge";
import CommunicationStatusPanel from "./CommunicationStatusPanel";
import { ExplainPaymentDialog, type ExplainPaymentRow } from "./ExplainPaymentDialog";
import { derivePopupPaymentState } from "@/lib/popupPaymentState";
import { Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const VENDOR_COMM_ENTITY = "popup_vendor_confirmation";

interface VendorRow {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  vendor_option: "rent" | "own";
  amount_due: number;
  agreement_initials: string;
  payment_status: "pending" | "paid" | "canceled";
  registration_status: "not_confirmed" | "confirmed" | "canceled";
  stripe_payment_id: string | null;
  admin_notes: string | null;
  review_status: "New" | "Reviewed";
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_at: string;
  updated_at: string | null;
}

interface TicketRow {
  id: string;
  full_name: string;
  email: string;
  ticket_quantity: number;
  amount_due: number;
  payment_status: "pending" | "paid" | "canceled";
  ticket_status: "not_issued" | "ready_to_send" | "sent" | "canceled";
  stripe_payment_id: string | null;
  ticket_sent_at: string | null;
  admin_notes: string | null;
  created_at: string;
  updated_at: string | null;
}

const MAX_VENDOR_KEY = "popup_max_vendor_spaces";

const formatMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const formatDate = (iso: string) => new Date(iso).toLocaleDateString();
const optionLabel = (o: string) =>
  o === "rent" ? "Rent a Table ($15)" : o === "own" ? "Bring Own ($5)" : o;
const statusColor = (s: string) => {
  if (s === "paid" || s === "confirmed" || s === "sent" || s === "ready_to_send")
    return "bg-green-100 text-green-800";
  if (s === "canceled") return "bg-gray-200 text-gray-700";
  return "bg-yellow-100 text-yellow-800";
};
const humanStatus = (s: string) =>
  s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function downloadCSV(filename: string, rows: string[][]) {
  const csv = rows
    .map((r) =>
      r
        .map((c) => {
          const s = String(c ?? "");
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(","),
    )
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

interface PopUpManagementProps {
  onViewItem?: (itemType: "popup_vendor" | "popup_ticket", itemId: string) => void;
  focusItem?: { type: "popup_vendor" | "popup_ticket"; id: string } | null;
  onFocusHandled?: () => void;
}

const PopUpManagement = ({ onViewItem, focusItem, onFocusHandled }: PopUpManagementProps = {}) => {
  const { toast } = useToast();
  const invalidateExecSnapshot = useInvalidateSnapshot();
  const { events: activityEvents, markItemsReviewed } = useAdminActivityBadges();

  // When a vendor row is reviewed, also clear any admin-activity events that
  // reference the same vendor so the sidebar Pop-Up badge stays in lock-step
  // with the vendor page and the recommendation card. All three surfaces
  // reconcile to "vendors with review_status != Reviewed".
  const clearVendorActivity = async (vendorIds: string[]) => {
    if (!vendorIds.length) return;
    const idSet = new Set(vendorIds);
    const eventIds = activityEvents
      .filter(
        (e) =>
          e.section === "popup" &&
          e.source_table === "popup_vendor_registrations" &&
          e.source_record_id &&
          idSet.has(e.source_record_id),
      )
      .map((e) => e.id);
    if (eventIds.length) await markItemsReviewed(eventIds);
  };
  const [vendors, setVendors] = useState<VendorRow[]>([]);
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [subTab, setSubTab] = useState<string>("vendors");
  const [vendorSearch, setVendorSearch] = useState("");
  const [ticketSearch, setTicketSearch] = useState("");
  const [vendorFilter, setVendorFilter] = useState<string>("all");
  const [ticketFilter, setTicketFilter] = useState<string>("all");
  const [vendorSort, setVendorSort] = useState<{ k: keyof VendorRow; d: "asc" | "desc" }>({
    k: "created_at",
    d: "desc",
  });
  const [ticketSort, setTicketSort] = useState<{ k: keyof TicketRow; d: "asc" | "desc" }>({
    k: "created_at",
    d: "desc",
  });

  const [maxSpaces, setMaxSpaces] = useState<number>(() => {
    const v = typeof window !== "undefined" ? localStorage.getItem(MAX_VENDOR_KEY) : null;
    return v ? parseInt(v, 10) : 20;
  });

  const [editingVendor, setEditingVendor] = useState<VendorRow | null>(null);
  const [editingTicket, setEditingTicket] = useState<TicketRow | null>(null);
  const [explainRow, setExplainRow] = useState<ExplainPaymentRow | null>(null);

  // Reusable communication tracking for vendor confirmation emails
  const { rows: vendorComm } = useCommunicationStatusList(VENDOR_COMM_ENTITY);
  const { setStatus: setCommStatus } = useSetCommunicationStatus();

  // Bulk selection
  const [selectedVendorIds, setSelectedVendorIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<
    null | "sent" | "pending" | "reviewed" | "new"
  >(null);
  const toggleVendorSelected = (id: string) => {
    setSelectedVendorIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearVendorSelection = () => setSelectedVendorIds(new Set());
  const applyBulkComm = async (target: "sent" | "pending") => {
    const ids = Array.from(selectedVendorIds);
    for (const id of ids) {
      await setCommStatus(VENDOR_COMM_ENTITY, id, target);
    }
    clearVendorSelection();
    setBulkAction(null);
    toast({
      title: target === "sent" ? "Marked as sent" : "Marked as pending",
      description: `${ids.length} vendor${ids.length === 1 ? "" : "s"} updated`,
    });
  };

  const applyBulkReview = async (target: "Reviewed" | "New") => {
    const ids = Array.from(selectedVendorIds);
    if (ids.length === 0) {
      setBulkAction(null);
      return;
    }
    const patch =
      target === "Reviewed"
        ? {
            review_status: "Reviewed" as const,
            reviewed_at: new Date().toISOString(),
          }
        : {
            review_status: "New" as const,
            reviewed_at: null,
            reviewed_by: null,
          };
    // Optimistic
    setVendors((prev) =>
      prev.map((v) => (ids.includes(v.id) ? { ...v, ...patch } : v)),
    );
    // reviewed_by handled server-side via auth uid where possible; we set it via update payload
    const { data: userRes } = await supabase.auth.getUser();
    const withActor =
      target === "Reviewed"
        ? { ...patch, reviewed_by: userRes.user?.id ?? null }
        : patch;
    const { error } = await supabase
      .from("popup_vendor_registrations")
      .update(withActor)
      .in("id", ids);
    if (error) {
      toast({
        title: "Bulk update failed",
        description: error.message,
        variant: "destructive",
      });
      fetchAll();
      return;
    }
    clearVendorSelection();
    setBulkAction(null);
    if (target === "Reviewed") {
      await clearVendorActivity(ids);
      invalidateExecSnapshot();
    }
    toast({
      title: target === "Reviewed" ? "Marked as reviewed" : "Marked as new",
      description: `${ids.length} vendor${ids.length === 1 ? "" : "s"} updated`,
    });
  };


  const openVendorEdit = (v: VendorRow) => {
    setEditingVendor(v);
    onViewItem?.("popup_vendor", v.id);
  };
  const openTicketEdit = (t: TicketRow) => {
    setEditingTicket(t);
    onViewItem?.("popup_ticket", t.id);
  };

  useEffect(() => {
    localStorage.setItem(MAX_VENDOR_KEY, String(maxSpaces));
  }, [maxSpaces]);

  const fetchAll = async () => {
    setLoading(true);
    const [{ data: v, error: ve }, { data: t, error: te }] = await Promise.all([
      supabase
        .from("popup_vendor_registrations")
        .select("*")
        .order("created_at", { ascending: false }),
      supabase
        .from("popup_early_bird_orders")
        .select("*")
        .order("created_at", { ascending: false }),
    ]);
    if (ve) toast({ title: "Failed to load vendors", description: ve.message, variant: "destructive" });
    else setVendors((v as VendorRow[]) || []);
    if (te) toast({ title: "Failed to load tickets", description: te.message, variant: "destructive" });
    else setTickets((t as TicketRow[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Realtime updates so new registrations appear without refresh.
  useEffect(() => {
    const channel = supabase
      .channel("popup-management-live")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "popup_vendor_registrations" },
        (payload) => {
          setVendors((prev) => {
            const row = (payload.new || payload.old) as VendorRow;
            if (payload.eventType === "DELETE") return prev.filter((r) => r.id !== row.id);
            const next = [...prev];
            const idx = next.findIndex((r) => r.id === row.id);
            if (idx >= 0) next[idx] = payload.new as VendorRow;
            else next.unshift(payload.new as VendorRow);
            return next;
          });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "popup_early_bird_orders" },
        (payload) => {
          setTickets((prev) => {
            const row = (payload.new || payload.old) as TicketRow;
            if (payload.eventType === "DELETE") return prev.filter((r) => r.id !== row.id);
            const next = [...prev];
            const idx = next.findIndex((r) => r.id === row.id);
            if (idx >= 0) next[idx] = payload.new as TicketRow;
            else next.unshift(payload.new as TicketRow);
            return next;
          });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // React to a focus request from the notification center: switch to the
  // right tab and open the row's edit dialog.
  useEffect(() => {
    if (!focusItem) return;
    if (focusItem.type === "popup_vendor") {
      setSubTab("vendors");
      const v = vendors.find((r) => r.id === focusItem.id);
      if (v) {
        setEditingVendor(v);
        onFocusHandled?.();
      }
    } else if (focusItem.type === "popup_ticket") {
      setSubTab("tickets");
      const t = tickets.find((r) => r.id === focusItem.id);
      if (t) {
        setEditingTicket(t);
        onFocusHandled?.();
      }
    }
  }, [focusItem, vendors, tickets, onFocusHandled]);

  // ---------- VENDORS ----------
  const filteredVendors = useMemo(() => {
    let rows = [...vendors];
    if (vendorFilter !== "all") {
      rows = rows.filter((r) => {
        switch (vendorFilter) {
          case "pending":
            return r.payment_status === "pending";
          case "paid":
            return r.payment_status === "paid";
          case "confirmed":
            return r.registration_status === "confirmed";
          case "canceled":
            return r.payment_status === "canceled" || r.registration_status === "canceled";
          case "rent":
            return r.vendor_option === "rent";
          case "own":
            return r.vendor_option === "own";
          case "email_sent":
            return vendorComm[r.id]?.status === "sent";
          case "email_pending":
            return vendorComm[r.id]?.status !== "sent";
          case "review_new":
            return r.review_status !== "Reviewed";
          case "review_reviewed":
            return r.review_status === "Reviewed";
        }
        return true;
      });
    }
    const q = vendorSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        [r.full_name, r.email, r.phone, r.admin_notes ?? ""].some((f) =>
          f.toLowerCase().includes(q),
        ),
      );
    }
    rows.sort((a, b) => {
      const av = a[vendorSort.k] as any;
      const bv = b[vendorSort.k] as any;
      if (av === bv) return 0;
      const cmp = av > bv ? 1 : -1;
      return vendorSort.d === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [vendors, vendorFilter, vendorSearch, vendorSort, vendorComm]);

  const vendorStats = useMemo(() => {
    const paid = vendors.filter((v) => v.payment_status === "paid");
    const active = vendors.filter((v) => v.registration_status !== "canceled");
    const emailSent = active.filter((v) => vendorComm[v.id]?.status === "sent").length;
    const emailPending = active.length - emailSent;
    const reviewNew = active.filter((v) => v.review_status !== "Reviewed").length;
    const reviewReviewed = active.filter((v) => v.review_status === "Reviewed").length;
    return {
      total: vendors.length,
      confirmed: vendors.filter((v) => v.registration_status === "confirmed").length,
      pending: vendors.filter((v) => v.payment_status === "pending").length,
      rent: vendors.filter((v) => v.vendor_option === "rent").length,
      own: vendors.filter((v) => v.vendor_option === "own").length,
      fees: paid.reduce((s, v) => s + (v.amount_due || 0), 0),
      remaining: Math.max(0, maxSpaces - active.length),
      emailSent,
      emailPending,
      reviewNew,
      reviewReviewed,
    };
  }, [vendors, maxSpaces, vendorComm]);

  const updateVendor = async (id: string, patch: Partial<VendorRow>) => {
    const { error } = await supabase
      .from("popup_vendor_registrations")
      .update(patch)
      .eq("id", id);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      return false;
    }
    setVendors((prev) => prev.map((v) => (v.id === id ? { ...v, ...patch } : v)));
    return true;
  };

  const setVendorReview = async (
    id: string,
    status: "New" | "Reviewed",
  ) => {
    const { data: userRes } = await supabase.auth.getUser();
    const patch =
      status === "Reviewed"
        ? {
            review_status: "Reviewed" as const,
            reviewed_at: new Date().toISOString(),
            reviewed_by: userRes.user?.id ?? null,
          }
        : {
            review_status: "New" as const,
            reviewed_at: null,
            reviewed_by: null,
          };
    // Optimistic
    setVendors((prev) =>
      prev.map((v) => (v.id === id ? { ...v, ...patch } : v)),
    );
    const { error } = await supabase
      .from("popup_vendor_registrations")
      .update(patch)
      .eq("id", id);
    if (error) {
      toast({
        title: "Update failed",
        description: error.message,
        variant: "destructive",
      });
      fetchAll();
      return false;
    }
    if (status === "Reviewed") {
      await clearVendorActivity([id]);
      invalidateExecSnapshot();
    }
    toast({
      title: status === "Reviewed" ? "Marked as reviewed" : "Marked as new",
    });
    return true;
  };

  const deleteVendor = async (id: string) => {
    if (!confirm("Delete this vendor registration? This cannot be undone.")) return;
    const { error } = await supabase.from("popup_vendor_registrations").delete().eq("id", id);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
      return;
    }
    setVendors((prev) => prev.filter((v) => v.id !== id));
    toast({ title: "Vendor deleted" });
  };

  const exportVendors = () => {
    const header = [
      "Date",
      "Full Name",
      "Email",
      "Phone",
      "Vendor Option",
      "Amount Due",
      "Initials",
      "Payment Status",
      "Registration Status",
      "Notes",
    ];
    const rows = filteredVendors.map((v) => [
      new Date(v.created_at).toISOString(),
      v.full_name,
      v.email,
      v.phone,
      optionLabel(v.vendor_option),
      formatMoney(v.amount_due),
      v.agreement_initials,
      humanStatus(v.payment_status),
      humanStatus(v.registration_status),
      v.admin_notes ?? "",
    ]);
    downloadCSV(`popup-vendors-${new Date().toISOString().slice(0, 10)}.csv`, [header, ...rows]);
  };

  // ---------- TICKETS ----------
  const filteredTickets = useMemo(() => {
    let rows = [...tickets];
    if (ticketFilter !== "all") {
      rows = rows.filter((r) => {
        switch (ticketFilter) {
          case "pending":
            return r.payment_status === "pending";
          case "paid":
            return r.payment_status === "paid";
          case "ready_to_send":
            return r.ticket_status === "ready_to_send";
          case "sent":
            return r.ticket_status === "sent";
          case "canceled":
            return r.payment_status === "canceled" || r.ticket_status === "canceled";
        }
        return true;
      });
    }
    const q = ticketSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        [r.full_name, r.email, r.admin_notes ?? ""].some((f) => f.toLowerCase().includes(q)),
      );
    }
    rows.sort((a, b) => {
      const av = a[ticketSort.k] as any;
      const bv = b[ticketSort.k] as any;
      if (av === bv) return 0;
      const cmp = av > bv ? 1 : -1;
      return ticketSort.d === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [tickets, ticketFilter, ticketSearch, ticketSort]);

  const ticketStats = useMemo(() => {
    const paid = tickets.filter((t) => t.payment_status === "paid");
    return {
      orders: tickets.length,
      totalTickets: tickets.reduce((s, t) => s + t.ticket_quantity, 0),
      paid: paid.length,
      pending: tickets.filter((t) => t.payment_status === "pending").length,
      ready: tickets.filter((t) => t.ticket_status === "ready_to_send").length,
      sent: tickets.filter((t) => t.ticket_status === "sent").length,
      revenue: paid.reduce((s, t) => s + (t.amount_due || 0), 0),
    };
  }, [tickets]);

  const updateTicket = async (id: string, patch: Partial<TicketRow>) => {
    const { error } = await supabase.from("popup_early_bird_orders").update(patch).eq("id", id);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      return false;
    }
    setTickets((prev) => prev.map((t) => (t.id === id ? { ...t, ...patch } : t)));
    return true;
  };

  const deleteTicket = async (id: string) => {
    if (!confirm("Delete this ticket order? This cannot be undone.")) return;
    const { error } = await supabase.from("popup_early_bird_orders").delete().eq("id", id);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
      return;
    }
    setTickets((prev) => prev.filter((t) => t.id !== id));
    toast({ title: "Ticket order deleted" });
  };

  const exportTickets = () => {
    const header = [
      "Date",
      "Full Name",
      "Email",
      "Quantity",
      "Amount Due",
      "Payment Status",
      "Ticket Status",
      "Ticket Sent At",
      "Notes",
    ];
    const rows = filteredTickets.map((t) => [
      new Date(t.created_at).toISOString(),
      t.full_name,
      t.email,
      String(t.ticket_quantity),
      formatMoney(t.amount_due),
      humanStatus(t.payment_status),
      humanStatus(t.ticket_status),
      t.ticket_sent_at ? new Date(t.ticket_sent_at).toISOString() : "",
      t.admin_notes ?? "",
    ]);
    downloadCSV(`popup-early-bird-${new Date().toISOString().slice(0, 10)}.csv`, [header, ...rows]);
  };

  const emailLink = (address: string, subject: string) =>
    `mailto:${address}?subject=${encodeURIComponent(subject)}`;

  const KpiCard = ({
    icon: Icon,
    label,
    value,
    subtitle,
    tone = "default",
    onClick,
  }: {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    value: string | number;
    subtitle?: string;
    tone?: "default" | "amber" | "green" | "rose" | "sage";
    onClick?: () => void;
  }) => {
    const toneMap: Record<string, string> = {
      default: "text-foreground",
      amber: "text-amber-700",
      green: "text-emerald-700",
      rose: "text-rose-700",
      sage: "text-primary",
    };
    return (
      <Card
        onClick={onClick}
        className={`border-border/60 shadow-sm rounded-2xl h-full transition-colors ${
          onClick ? "cursor-pointer hover:border-primary/60" : ""
        }`}
      >
        <CardContent className="p-5 flex flex-col gap-2">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Icon className={`h-4 w-4 ${toneMap[tone]}`} />
            <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
          </div>
          <div className={`text-3xl font-semibold leading-none ${toneMap[tone]}`}>{value}</div>
          {subtitle && <div className="text-xs text-muted-foreground">{subtitle}</div>}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <Tabs value={subTab} onValueChange={setSubTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="vendors">Vendor Registrations ({vendors.length})</TabsTrigger>
          <TabsTrigger value="tickets">Early Bird Tickets ({tickets.length})</TabsTrigger>
        </TabsList>

          {/* ================= VENDORS ================= */}
          <TabsContent value="vendors" className="space-y-6">
            {/* Summary cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard icon={Users} label="Vendors" value={vendorStats.total} tone="sage" />
              <KpiCard
                icon={CheckCircle2}
                label="Confirmed"
                value={vendorStats.confirmed}
                tone="green"
              />
              <KpiCard
                icon={AlertTriangle}
                label="Needs Review"
                value={vendorStats.reviewNew}
                tone={vendorStats.reviewNew > 0 ? "amber" : "default"}
                onClick={() => setVendorFilter("review_new")}
              />
              <KpiCard
                icon={DollarSign}
                label="Fees Collected"
                value={formatMoney(vendorStats.fees)}
                tone="green"
              />
              <KpiCard
                icon={Armchair}
                label="Spaces Remaining"
                value={`${vendorStats.remaining} / ${maxSpaces}`}
                subtitle={`${maxSpaces - vendorStats.remaining} taken`}
              />
              <Card
                onClick={() =>
                  setVendorFilter(
                    vendorStats.emailPending > 0 ? "email_pending" : "email_sent",
                  )
                }
                className="border-border/60 shadow-sm rounded-2xl h-full cursor-pointer hover:border-primary/60 transition-colors"
              >
                <CardContent className="p-5 flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <span className="text-xs font-medium uppercase tracking-wide">
                      Confirmation Emails
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-1">
                    <span className="text-sm text-rose-700 font-medium">
                      🔴 Pending: <span className="font-semibold">{vendorStats.emailPending}</span>
                    </span>
                    <span className="text-sm text-emerald-700 font-medium">
                      🟢 Sent: <span className="font-semibold">{vendorStats.emailSent}</span>
                    </span>
                  </div>
                </CardContent>
              </Card>
              <KpiCard
                icon={Ticket}
                label="Early Bird Tickets"
                value={`${ticketStats.totalTickets} sold`}
                subtitle={`${ticketStats.orders} orders`}
                onClick={() => setSubTab("tickets")}
              />
              <KpiCard
                icon={Armchair}
                label="Bring Your Own Table"
                value={vendorStats.own}
                subtitle={`${vendorStats.rent} rentals`}
              />
            </div>

            {/* Needs Attention */}
            {(vendorStats.reviewNew > 0 ||
              vendorStats.emailPending > 0 ||
              vendorStats.remaining <= 5) && (
              <div className="space-y-2">
                <h2 className="font-heading text-lg text-foreground">Needs Attention</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {vendorStats.reviewNew > 0 && (
                    <button
                      type="button"
                      onClick={() => setVendorFilter("review_new")}
                      className="text-left rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 hover:border-amber-300 transition-colors"
                    >
                      <div className="text-xs font-medium uppercase tracking-wide text-amber-700">
                        🟠 Awaiting Review
                      </div>
                      <div className="text-sm text-amber-900 mt-1">
                        {vendorStats.reviewNew} vendor{vendorStats.reviewNew === 1 ? "" : "s"} to review
                      </div>
                    </button>
                  )}
                  {vendorStats.emailPending > 0 && (
                    <button
                      type="button"
                      onClick={() => setVendorFilter("email_pending")}
                      className="text-left rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 hover:border-rose-300 transition-colors"
                    >
                      <div className="text-xs font-medium uppercase tracking-wide text-rose-700">
                        🔴 Confirmation Emails
                      </div>
                      <div className="text-sm text-rose-900 mt-1">
                        {vendorStats.emailPending} email{vendorStats.emailPending === 1 ? "" : "s"} need sending
                      </div>
                    </button>
                  )}
                  {vendorStats.remaining <= 5 && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3">
                      <div className="text-xs font-medium uppercase tracking-wide text-amber-700">
                        🟡 Capacity
                      </div>
                      <div className="text-sm text-amber-900 mt-1">
                        Only {vendorStats.remaining} space{vendorStats.remaining === 1 ? "" : "s"} remaining
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Toolbar */}
            <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border/60 bg-card/60 p-3">
              <div className="space-y-1 flex-1 min-w-[220px]">
                <Label className="text-xs">Search</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="pl-8"
                    placeholder="Name, email, phone, notes…"
                    value={vendorSearch}
                    onChange={(e) => setVendorSearch(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Filter</Label>
                <Select value={vendorFilter} onValueChange={setVendorFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pending">Payment Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="canceled">Canceled</SelectItem>
                    <SelectItem value="rent">Rent a Table</SelectItem>
                    <SelectItem value="own">Bring Your Own Table</SelectItem>
                    <SelectItem value="email_sent">🟢 Email Sent</SelectItem>
                    <SelectItem value="email_pending">🔴 Email Pending</SelectItem>
                    <SelectItem value="review_new">🔴 Needs Review</SelectItem>
                    <SelectItem value="review_reviewed">🟢 Reviewed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Max Spaces</Label>
                <Input
                  type="number"
                  min={0}
                  className="w-24"
                  value={maxSpaces}
                  onChange={(e) => setMaxSpaces(Math.max(0, parseInt(e.target.value || "0", 10)))}
                />
              </div>
              <div className="ml-auto flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />
                  Refresh
                </Button>
                <Button variant="outline" size="sm" onClick={exportVendors}>
                  <Download className="h-4 w-4 mr-1" /> Export CSV
                </Button>
              </div>
            </div>


            {selectedVendorIds.size > 0 && (
              <div className="flex items-center justify-between rounded-md border bg-muted/40 px-3 py-2">
                <div className="text-sm">
                  <strong>{selectedVendorIds.size}</strong> vendor
                  {selectedVendorIds.size === 1 ? "" : "s"} selected
                </div>
                <div className="flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm">Bulk actions</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setBulkAction("reviewed")}>
                        <Eye className="h-4 w-4 mr-2" />
                        Mark Selected as Reviewed
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setBulkAction("new")}>
                        <EyeOff className="h-4 w-4 mr-2" />
                        Mark Selected as New
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setBulkAction("sent")}>
                        <MailCheck className="h-4 w-4 mr-2" />
                        Mark Confirmation Email as Sent
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setBulkAction("pending")}>
                        <Mail className="h-4 w-4 mr-2" />
                        Mark Confirmation Email as Pending
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button size="sm" variant="ghost" onClick={clearVendorSelection}>
                    Clear
                  </Button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox
                        checked={
                          filteredVendors.length > 0 &&
                          filteredVendors.every((v) => selectedVendorIds.has(v.id))
                        }
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedVendorIds(new Set(filteredVendors.map((v) => v.id)));
                          } else {
                            clearVendorSelection();
                          }
                        }}
                        aria-label="Select all"
                      />
                    </TableHead>
                    <TableHead>
                      <button
                        className="flex items-center gap-1"
                        onClick={() =>
                          setVendorSort((s) => ({
                            k: "created_at",
                            d: s.k === "created_at" && s.d === "desc" ? "asc" : "desc",
                          }))
                        }
                      >
                        Date <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button
                        className="flex items-center gap-1"
                        onClick={() =>
                          setVendorSort((s) => ({
                            k: "full_name",
                            d: s.k === "full_name" && s.d === "asc" ? "desc" : "asc",
                          }))
                        }
                      >
                        Name <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Option</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Initials</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Registration</TableHead>
                    <TableHead>Review</TableHead>
                    <TableHead>Email Status</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredVendors.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={14} className="text-center text-muted-foreground py-6">
                        {loading ? "Loading…" : "No vendor registrations."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredVendors.map((v) => (
                      <TableRow key={v.id} data-state={selectedVendorIds.has(v.id) ? "selected" : undefined}>
                        <TableCell>
                          <Checkbox
                            checked={selectedVendorIds.has(v.id)}
                            onCheckedChange={() => toggleVendorSelected(v.id)}
                            aria-label={`Select ${v.full_name}`}
                          />
                        </TableCell>
                        <TableCell className="text-sm">{formatDate(v.created_at)}</TableCell>
                        <TableCell className="font-medium">{v.full_name}</TableCell>
                        <TableCell className="text-sm">{v.email}</TableCell>
                        <TableCell className="text-sm">{v.phone}</TableCell>
                        <TableCell className="text-sm">{optionLabel(v.vendor_option)}</TableCell>
                        <TableCell>{formatMoney(v.amount_due)}</TableCell>
                        <TableCell className="text-sm uppercase">{v.agreement_initials}</TableCell>
                        <TableCell>
                          {(() => {
                            const st = derivePopupPaymentState(v);
                            return (
                              <TooltipProvider delayDuration={150}>
                                <div className="flex items-center gap-1">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-help ${st.className}`}>
                                        <span>{st.dot}</span>
                                        {st.label}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">{st.tooltip}</TooltipContent>
                                  </Tooltip>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-1 text-xs"
                                    onClick={() => setExplainRow({ ...v })}
                                    title="Explain payment status"
                                  >
                                    <Info className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </TooltipProvider>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(v.registration_status)}`}>
                            {humanStatus(v.registration_status)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              v.review_status === "Reviewed"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                            title={
                              v.review_status === "Reviewed" && v.reviewed_at
                                ? `Reviewed ${new Date(v.reviewed_at).toLocaleString()}`
                                : "Awaiting review"
                            }
                          >
                            {v.review_status === "Reviewed" ? "🟢 Reviewed" : "🔴 New"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <CommunicationStatusBadge row={vendorComm[v.id]} compact />
                        </TableCell>
                        <TableCell className="max-w-[160px] truncate text-xs text-muted-foreground" title={v.admin_notes ?? ""}>
                          {v.admin_notes || "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {v.review_status !== "Reviewed" ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Mark as reviewed"
                                onClick={() => setVendorReview(v.id, "Reviewed")}
                              >
                                <Eye className="h-4 w-4 text-green-600" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Mark as new"
                                onClick={() => setVendorReview(v.id, "New")}
                              >
                                <EyeOff className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            )}
                            {v.payment_status !== "paid" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Mark as paid"
                                onClick={() => updateVendor(v.id, { payment_status: "paid" })}
                              >
                                <Check className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                            {v.registration_status !== "confirmed" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Mark as confirmed"
                                onClick={() => updateVendor(v.id, { registration_status: "confirmed" })}
                              >
                                <Check className="h-4 w-4 text-primary" />
                              </Button>
                            )}
                            {v.registration_status !== "canceled" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Mark as canceled"
                                onClick={() =>
                                  updateVendor(v.id, {
                                    registration_status: "canceled",
                                    payment_status: "canceled",
                                  })
                                }
                              >
                                <X className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            )}
                            <a href={emailLink(v.email, "Silver Sage Pop-Up Vendor Registration")}>
                              <Button variant="ghost" size="sm" title="Email vendor">
                                <Mail className="h-4 w-4" />
                              </Button>
                            </a>
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Edit / notes"
                              onClick={() => openVendorEdit(v)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                              title="Delete"
                              onClick={() => deleteVendor(v.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* ================= TICKETS ================= */}
          <TabsContent value="tickets" className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard icon={Ticket} label="Orders" value={ticketStats.orders} tone="sage" />
              <KpiCard icon={Ticket} label="Tickets Sold" value={ticketStats.totalTickets} />
              <KpiCard icon={CheckCircle2} label="Paid" value={ticketStats.paid} tone="green" />
              <KpiCard icon={AlertTriangle} label="Awaiting Payment" value={ticketStats.pending} tone={ticketStats.pending > 0 ? "amber" : "default"} />
              <KpiCard icon={Send} label="Ready to Send" value={ticketStats.ready} />
              <KpiCard icon={MailCheck} label="Sent" value={ticketStats.sent} tone="green" />
              <KpiCard icon={DollarSign} label="Revenue" value={formatMoney(ticketStats.revenue)} tone="green" />
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1 flex-1 min-w-[200px]">
                <Label className="text-xs">Search</Label>
                <Input
                  placeholder="Name, email, notes…"
                  value={ticketSearch}
                  onChange={(e) => setTicketSearch(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Filter</Label>
                <Select value={ticketFilter} onValueChange={setTicketFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="pending">Payment Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="ready_to_send">Ready to Send</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="canceled">Canceled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={exportTickets}>
                <Download className="h-4 w-4 mr-1" /> Export CSV
              </Button>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button
                        className="flex items-center gap-1"
                        onClick={() =>
                          setTicketSort((s) => ({
                            k: "created_at",
                            d: s.k === "created_at" && s.d === "desc" ? "asc" : "desc",
                          }))
                        }
                      >
                        Date <ArrowUpDown className="h-3 w-3" />
                      </button>
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Payment</TableHead>
                    <TableHead>Ticket</TableHead>
                    <TableHead>Sent</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTickets.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-muted-foreground py-6">
                        {loading ? "Loading…" : "No ticket orders."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredTickets.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell className="text-sm">{formatDate(t.created_at)}</TableCell>
                        <TableCell className="font-medium">{t.full_name}</TableCell>
                        <TableCell className="text-sm">{t.email}</TableCell>
                        <TableCell>{t.ticket_quantity}</TableCell>
                        <TableCell>{formatMoney(t.amount_due)}</TableCell>
                        <TableCell>
                          {(() => {
                            const st = derivePopupPaymentState(t);
                            return (
                              <TooltipProvider delayDuration={150}>
                                <div className="flex items-center gap-1">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium cursor-help ${st.className}`}>
                                        <span>{st.dot}</span>
                                        {st.label}
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-xs">{st.tooltip}</TooltipContent>
                                  </Tooltip>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-1 text-xs"
                                    onClick={() => setExplainRow({ ...t })}
                                    title="Explain payment status"
                                  >
                                    <Info className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </TooltipProvider>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor(t.ticket_status)}`}>
                            {humanStatus(t.ticket_status)}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs">
                          {t.ticket_sent_at ? formatDate(t.ticket_sent_at) : "—"}
                        </TableCell>
                        <TableCell className="max-w-[160px] truncate text-xs text-muted-foreground" title={t.admin_notes ?? ""}>
                          {t.admin_notes || "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {t.payment_status !== "paid" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Mark as paid"
                                onClick={() =>
                                  updateTicket(t.id, {
                                    payment_status: "paid",
                                    ticket_status: t.ticket_status === "sent" ? "sent" : "ready_to_send",
                                  })
                                }
                              >
                                <Check className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                            {t.ticket_status !== "sent" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Mark ticket as sent"
                                onClick={() =>
                                  updateTicket(t.id, {
                                    ticket_status: "sent",
                                    ticket_sent_at: new Date().toISOString(),
                                  })
                                }
                              >
                                <Send className="h-4 w-4 text-primary" />
                              </Button>
                            )}
                            {t.ticket_status === "sent" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Resend ticket"
                                onClick={() =>
                                  updateTicket(t.id, { ticket_sent_at: new Date().toISOString() })
                                }
                              >
                                <RefreshCw className="h-4 w-4 text-primary" />
                              </Button>
                            )}
                            {t.ticket_status !== "canceled" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Mark as canceled"
                                onClick={() =>
                                  updateTicket(t.id, {
                                    payment_status: "canceled",
                                    ticket_status: "canceled",
                                  })
                                }
                              >
                                <X className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            )}
                            <a href={emailLink(t.email, "Silver Sage Pop-Up Early Bird Ticket")}>
                              <Button variant="ghost" size="sm" title="Email purchaser">
                                <Mail className="h-4 w-4" />
                              </Button>
                            </a>
                            <Button
                              variant="ghost"
                              size="sm"
                              title="Edit / notes"
                              onClick={() => openTicketEdit(t)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                              title="Delete"
                              onClick={() => deleteTicket(t.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>

      {/* Vendor edit dialog */}
      <Dialog open={!!editingVendor} onOpenChange={(o) => !o && setEditingVendor(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">

          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="h-4 w-4" /> Edit vendor registration
            </DialogTitle>
          </DialogHeader>
          {editingVendor && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Full Name</Label>
                  <Input
                    value={editingVendor.full_name}
                    onChange={(e) =>
                      setEditingVendor({ ...editingVendor, full_name: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-1">
                  <Label>Phone</Label>
                  <Input
                    value={editingVendor.phone}
                    onChange={(e) =>
                      setEditingVendor({ ...editingVendor, phone: e.target.value })
                    }
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Email</Label>
                <Input
                  value={editingVendor.email}
                  onChange={(e) =>
                    setEditingVendor({ ...editingVendor, email: e.target.value })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Vendor Option</Label>
                  <Select
                    value={editingVendor.vendor_option}
                    onValueChange={(v: "rent" | "own") =>
                      setEditingVendor({
                        ...editingVendor,
                        vendor_option: v,
                        amount_due: v === "rent" ? 1500 : 500,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="rent">Rent ($15)</SelectItem>
                      <SelectItem value="own">Own ($5)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Amount Due (cents)</Label>
                  <Input
                    type="number"
                    value={editingVendor.amount_due}
                    onChange={(e) =>
                      setEditingVendor({
                        ...editingVendor,
                        amount_due: parseInt(e.target.value || "0", 10),
                      })
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Payment Status</Label>
                  <Select
                    value={editingVendor.payment_status}
                    onValueChange={(v: any) =>
                      setEditingVendor({ ...editingVendor, payment_status: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="canceled">Canceled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Registration Status</Label>
                  <Select
                    value={editingVendor.registration_status}
                    onValueChange={(v: any) =>
                      setEditingVendor({ ...editingVendor, registration_status: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_confirmed">Not Confirmed</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="canceled">Canceled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-md border bg-muted/30 px-3 py-2">
                <div className="text-sm">
                  <span className="font-medium">Review status:</span>{" "}
                  {editingVendor.review_status === "Reviewed" ? (
                    <span className="text-green-700">
                      🟢 Reviewed
                      {editingVendor.reviewed_at
                        ? ` · ${new Date(editingVendor.reviewed_at).toLocaleString()}`
                        : ""}
                    </span>
                  ) : (
                    <span className="text-red-700">🔴 New</span>
                  )}
                </div>
                {editingVendor.review_status === "Reviewed" ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const ok = await setVendorReview(editingVendor.id, "New");
                      if (ok)
                        setEditingVendor({
                          ...editingVendor,
                          review_status: "New",
                          reviewed_at: null,
                          reviewed_by: null,
                        });
                    }}
                  >
                    <EyeOff className="h-4 w-4 mr-1" /> Mark as New
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={async () => {
                      const ok = await setVendorReview(editingVendor.id, "Reviewed");
                      if (ok)
                        setEditingVendor({
                          ...editingVendor,
                          review_status: "Reviewed",
                          reviewed_at: new Date().toISOString(),
                        });
                    }}
                  >
                    <Eye className="h-4 w-4 mr-1" /> Mark as Reviewed
                  </Button>
                )}
              </div>
              <div className="space-y-1">
                <Label>Internal Notes</Label>
                <Textarea
                  rows={3}
                  value={editingVendor.admin_notes ?? ""}
                  onChange={(e) =>
                    setEditingVendor({ ...editingVendor, admin_notes: e.target.value })
                  }
                />
              </div>
              <CommunicationStatusPanel
                entityType={VENDOR_COMM_ENTITY}
                entityId={editingVendor.id}
                row={vendorComm[editingVendor.id]}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingVendor(null)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!editingVendor) return;
                const ok = await updateVendor(editingVendor.id, {
                  full_name: editingVendor.full_name,
                  email: editingVendor.email,
                  phone: editingVendor.phone,
                  vendor_option: editingVendor.vendor_option,
                  amount_due: editingVendor.amount_due,
                  payment_status: editingVendor.payment_status,
                  registration_status: editingVendor.registration_status,
                  admin_notes: editingVendor.admin_notes,
                });
                if (ok) {
                  setEditingVendor(null);
                  toast({ title: "Saved" });
                }
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket edit dialog */}
      <Dialog open={!!editingTicket} onOpenChange={(o) => !o && setEditingTicket(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <StickyNote className="h-4 w-4" /> Edit ticket order
            </DialogTitle>
          </DialogHeader>
          {editingTicket && (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Full Name</Label>
                <Input
                  value={editingTicket.full_name}
                  onChange={(e) =>
                    setEditingTicket({ ...editingTicket, full_name: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Email</Label>
                <Input
                  value={editingTicket.email}
                  onChange={(e) =>
                    setEditingTicket({ ...editingTicket, email: e.target.value })
                  }
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Ticket Quantity</Label>
                  <Input
                    type="number"
                    min={1}
                    value={editingTicket.ticket_quantity}
                    onChange={(e) => {
                      const q = Math.max(1, parseInt(e.target.value || "1", 10));
                      setEditingTicket({
                        ...editingTicket,
                        ticket_quantity: q,
                        amount_due: q * 1500,
                      });
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Amount Due (cents)</Label>
                  <Input
                    type="number"
                    value={editingTicket.amount_due}
                    onChange={(e) =>
                      setEditingTicket({
                        ...editingTicket,
                        amount_due: parseInt(e.target.value || "0", 10),
                      })
                    }
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Payment Status</Label>
                  <Select
                    value={editingTicket.payment_status}
                    onValueChange={(v: any) =>
                      setEditingTicket({ ...editingTicket, payment_status: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="canceled">Canceled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Ticket Status</Label>
                  <Select
                    value={editingTicket.ticket_status}
                    onValueChange={(v: any) =>
                      setEditingTicket({ ...editingTicket, ticket_status: v })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_issued">Not Issued</SelectItem>
                      <SelectItem value="ready_to_send">Ready to Send</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="canceled">Canceled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Internal Notes</Label>
                <Textarea
                  rows={3}
                  value={editingTicket.admin_notes ?? ""}
                  onChange={(e) =>
                    setEditingTicket({ ...editingTicket, admin_notes: e.target.value })
                  }
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTicket(null)}>
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!editingTicket) return;
                const ok = await updateTicket(editingTicket.id, {
                  full_name: editingTicket.full_name,
                  email: editingTicket.email,
                  ticket_quantity: editingTicket.ticket_quantity,
                  amount_due: editingTicket.amount_due,
                  payment_status: editingTicket.payment_status,
                  ticket_status: editingTicket.ticket_status,
                  admin_notes: editingTicket.admin_notes,
                });
                if (ok) {
                  setEditingTicket(null);
                  toast({ title: "Saved" });
                }
              }}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk action confirmation */}
      <AlertDialog open={!!bulkAction} onOpenChange={(o) => !o && setBulkAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {bulkAction === "sent"
                ? "Mark selected vendors as Confirmation Email Sent?"
                : bulkAction === "pending"
                ? "Mark selected vendors as Confirmation Email Pending?"
                : bulkAction === "reviewed"
                ? "Mark selected vendors as Reviewed?"
                : "Mark selected vendors as New?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will update {selectedVendorIds.size} vendor
              {selectedVendorIds.size === 1 ? "" : "s"}.
              {(bulkAction === "sent" || bulkAction === "pending") &&
                " Each change is recorded in the email audit log. This does not send an actual email."}
              {(bulkAction === "reviewed" || bulkAction === "new") &&
                " Review status is independent from confirmation email tracking."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (bulkAction === "sent" || bulkAction === "pending") {
                  applyBulkComm(bulkAction);
                } else if (bulkAction === "reviewed") {
                  applyBulkReview("Reviewed");
                } else if (bulkAction === "new") {
                  applyBulkReview("New");
                }
              }}
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ExplainPaymentDialog
        open={!!explainRow}
        onOpenChange={(o) => !o && setExplainRow(null)}
        row={explainRow}
      />
    </div>
  );
};

export default PopUpManagement;
